import { Card, DatePicker, Select, Checkbox, Button, Table, Space, message, Statistic, Row, Col } from 'antd'
import { BarChartOutlined } from '@ant-design/icons'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import dayjs from 'dayjs'

const { RangePicker } = DatePicker

function Sales() {
  const { t } = useTranslation()
  const [dateRange, setDateRange] = useState([dayjs().startOf('month'), dayjs()])
  const [groupBy, setGroupBy] = useState('daily')
  const [warehouses, setWarehouses] = useState([])
  const [selectedWarehouse, setSelectedWarehouse] = useState(null)
  const [reps, setReps] = useState([])
  const [selectedRep, setSelectedRep] = useState(null)
  const [billTypes, setBillTypes] = useState([])
  const [selectedType, setSelectedType] = useState([])
  const [breakdownWarehouse, setBreakdownWarehouse] = useState(false)
  const [breakdownPayment, setBreakdownPayment] = useState(false)
  const [breakdownRep, setBreakdownRep] = useState(false)
  const [breakdownType, setBreakdownType] = useState(false)
  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState([])
  const [grandTotal, setGrandTotal] = useState(0)
  const [totalBillCount, setTotalBillCount] = useState(0)

  const token = localStorage.getItem('token')
  const headers = { Authorization: `Bearer ${token}` }

  useEffect(() => {
    fetchWarehouses()
    fetchReps()
    fetchBillTypes()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchWarehouses = async () => {
    try {
      const res = await axios.get('/api/settings/reports/warehouses', { headers })
      setWarehouses(res.data)
    } catch (err) {
      message.error(t('reports.errors.warehouses'))
    }
  }

  const fetchReps = async () => {
    try {
      const res = await axios.get('/api/settings/reports/reps', { headers })
      setReps(res.data)
    } catch (err) {
      message.error(t('reports.errors.reps'))
    }
  }

  const fetchBillTypes = async () => {
    try {
      const res = await axios.get('/api/settings/reports/sales-bill-types', { headers })
      setBillTypes(res.data)
    } catch (err) {
      message.error(t('reports.errors.salesBillTypes'))
    }
  }

  const fetchReport = async () => {
    setLoading(true)
    try {
      const params = {
        date_from: dayjs(dateRange[0]).format('YYYY-MM-DD'),
        date_to: dayjs(dateRange[1]).format('YYYY-MM-DD'),
        group_by: groupBy,
        breakdown_warehouse: breakdownWarehouse,
        breakdown_payment: breakdownPayment,
        breakdown_rep: breakdownRep,
        breakdown_type: breakdownType
      }
      if (selectedWarehouse) params.warehouse = selectedWarehouse
      if (selectedRep) params.rep = selectedRep
      // نرسل الأنماط المختارة كنص مفصول بفواصل — الباك إند يفكّكها
      // إلى معاملات SQL منفصلة (نفس نمط الإرسال البسيط المستخدَم
      // لباقي المعاملات هنا، بدون تعقيد axios بصيغة مصفوفات)
      if (selectedType.length > 0) params.type_guid = selectedType.join(',')

      const res = await axios.get('/api/settings/reports/sales', { headers, params })
      setRows(res.data.rows)
      setGrandTotal(res.data.grandTotal)
      setTotalBillCount(res.data.totalBillCount)
    } catch (err) {
      // رسائل الباك إند تبقى عربية دائماً بغض النظر عن لغة الواجهة
      // (قرار مقصود لتفادي تعقيد ترجمة الباك إند حالياً)، وفي حال
      // عدم وجود رسالة من الباك إند (مثل انقطاع الشبكة) نستخدم رسالة
      // مترجمة حسب لغة الواجهة الحالية
      message.error(err.response?.data?.error || t('reports.errors.salesReport'))
    }
    setLoading(false)
  }

  // نجمّع الصفوف حسب الفترة فقط (بغض النظر عن أي تفصيل مفعَّل) لعرض
  // رسم بياني واحد واضح للإجمالي عبر الزمن؛ التفصيل الكامل يظهر
  // في الجدول أسفله بدل تعقيد الرسم البياني بعدة سلاسل متداخلة
  const chartData = Object.values(
    rows.reduce((acc, r) => {
      if (!acc[r.period]) acc[r.period] = { period: r.period, total: 0 }
      acc[r.period].total += r.total
      return acc
    }, {})
  )
  const maxValue = Math.max(...chartData.map(d => d.total), 1)

  // أعمدة الجدول تُبنى ديناميكياً حسب خيارات التفصيل المفعَّلة
  const columns = [
    { title: <span style={{ color: '#1c599e' }}>{t('reports.columnPeriod')}</span>, dataIndex: 'period', key: 'period' },
    ...(breakdownWarehouse ? [{ title: <span style={{ color: '#1c599e' }}>{t('reports.columnWarehouse')}</span>, dataIndex: 'warehouse', key: 'warehouse' }] : []),
    ...(breakdownPayment ? [{ title: <span style={{ color: '#1c599e' }}>{t('reports.columnPaymentMethod')}</span>, dataIndex: 'payType', key: 'payType' }] : []),
    ...(breakdownRep ? [{ title: <span style={{ color: '#1c599e' }}>{t('reports.columnRep')}</span>, dataIndex: 'rep', key: 'rep', render: v => v || '-' }] : []),
    ...(breakdownType ? [{ title: <span style={{ color: '#1c599e' }}>{t('reports.columnBillType')}</span>, dataIndex: 'billType', key: 'billType' }] : []),
    { title: <span style={{ color: '#1c599e' }}>{t('reports.columnBillCount')}</span>, dataIndex: 'billCount', key: 'billCount', width: 110,
      render: v => <span style={{ color: '#555' }}>{v}</span> },
    {
      title: <span style={{ color: '#1c599e' }}>{t('reports.columnTotal')}</span>,
      dataIndex: 'total',
      key: 'total',
      render: v => <strong style={{ color: '#0f8a45' }}>{v.toLocaleString()} {t('reports.currency')}</strong>
    },
  ]

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <Space size="middle" wrap align="end">
          <div>
            <div style={{ fontSize: 12, color: '#1c599e', fontWeight: 600, marginBottom: 4 }}>{t('reports.period')}</div>
            <RangePicker value={dateRange} onChange={setDateRange} />
          </div>

          <div>
            <div style={{ fontSize: 12, color: '#1c599e', fontWeight: 600, marginBottom: 4 }}>{t('reports.groupBy')}</div>
            <Select
              value={groupBy}
              style={{ width: 140 }}
              onChange={setGroupBy}
              options={[
                { label: t('reports.daily'), value: 'daily' },
                { label: t('reports.monthly'), value: 'monthly' },
                { label: t('reports.quarterly'), value: 'quarterly' },
                { label: t('reports.semiannual'), value: 'semiannual' },
                { label: t('reports.yearly'), value: 'yearly' },
              ]}
            />
          </div>

          <div>
            <div style={{ fontSize: 12, color: '#1c599e', fontWeight: 600, marginBottom: 4 }}>{t('reports.warehouseOptional')}</div>
            <Select
              value={selectedWarehouse}
              style={{ width: 180 }}
              allowClear
              placeholder={t('reports.allWarehouses')}
              onChange={setSelectedWarehouse}
              options={warehouses.map(w => ({ label: w.StName, value: w.StoreGuid }))}
            />
          </div>

          <div>
            <div style={{ fontSize: 12, color: '#1c599e', fontWeight: 600, marginBottom: 4 }}>{t('reports.repOptional')}</div>
            <Select
              value={selectedRep}
              style={{ width: 180 }}
              allowClear
              placeholder={t('reports.allReps')}
              onChange={setSelectedRep}
              options={reps.map(r => ({ label: r.RepName, value: r.RepGuid }))}
            />
          </div>

          <div>
            <div style={{ fontSize: 12, color: '#1c599e', fontWeight: 600, marginBottom: 4 }}>{t('reports.billTypesOptional')}</div>
            <Select
              mode="multiple"
              value={selectedType}
              style={{ width: 240 }}
              allowClear
              placeholder={t('reports.allTypes')}
              maxTagCount="responsive"
              onChange={setSelectedType}
              options={billTypes.map(bt => ({ label: bt.BtName, value: bt.TypeGuid }))}
            />
          </div>

          <div>
            {/* عنصر فارغ بنفس ارتفاع تسميات الحقول الأخرى، لضمان محاذاة
                الزر تماماً مع الحقول المجاورة بدل الالتصاق بأسفلها فقط */}
            <div style={{ fontSize: 12, marginBottom: 4 }}>&nbsp;</div>
            <Button type="primary" icon={<BarChartOutlined />} loading={loading} onClick={fetchReport}>
              {t('reports.showReport')}
            </Button>
          </div>
        </Space>

        <div style={{ marginTop: 16 }}>
          <Space size="large">
            <Checkbox checked={breakdownWarehouse} onChange={e => setBreakdownWarehouse(e.target.checked)}>
              <span style={{ color: '#1c599e', fontWeight: 500 }}>{t('reports.breakdownWarehouse')}</span>
            </Checkbox>
            <Checkbox checked={breakdownPayment} onChange={e => setBreakdownPayment(e.target.checked)}>
              <span style={{ color: '#1c599e', fontWeight: 500 }}>{t('reports.breakdownPayment')}</span>
            </Checkbox>
            <Checkbox checked={breakdownRep} onChange={e => setBreakdownRep(e.target.checked)}>
              <span style={{ color: '#1c599e', fontWeight: 500 }}>{t('reports.breakdownRep')}</span>
            </Checkbox>
            <Checkbox checked={breakdownType} onChange={e => setBreakdownType(e.target.checked)}>
              <span style={{ color: '#1c599e', fontWeight: 500 }}>{t('reports.breakdownType')}</span>
            </Checkbox>
          </Space>
        </div>
      </Card>

      {rows.length > 0 && (
        <>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={12}>
              <Card>
                <Statistic
                  title={<span style={{ color: '#555', fontWeight: 600 }}>{t('reports.totalSales')}</span>}
                  value={grandTotal}
                  precision={2}
                  suffix={t('reports.currency')}
                  valueStyle={{ color: '#0f8a45', fontWeight: 'bold' }}
                />
              </Card>
            </Col>
            <Col span={12}>
              <Card>
                <Statistic
                  title={<span style={{ color: '#555', fontWeight: 600 }}>{t('reports.billCount')}</span>}
                  value={totalBillCount}
                  valueStyle={{ color: '#1c599e', fontWeight: 'bold' }}
                />
              </Card>
            </Col>
          </Row>

          <Card title={<span style={{ color: '#1c599e' }}>{t('reports.salesOverTime')}</span>} style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 220, overflowX: 'auto', padding: '0 8px' }}>
              {chartData.map((d) => (
                <div key={d.period} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 60 }}>
                  <div style={{ fontSize: 11, marginBottom: 4, color: '#0f8a45', fontWeight: 600 }}>
                    {d.total.toLocaleString()}
                  </div>
                  <div
                    style={{
                      width: 36,
                      height: `${(d.total / maxValue) * 160}px`,
                      background: 'linear-gradient(180deg, #0f8a45, #4ecb7a)',
                      borderRadius: '4px 4px 0 0',
                      minHeight: 2
                    }}
                  />
                  <div style={{ fontSize: 11, marginTop: 6, color: '#1c599e', fontWeight: 500 }}>
                    {d.period}
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card title={<span style={{ color: '#1c599e' }}>{t('reports.details')}</span>}>
            <Table
              columns={columns}
              dataSource={rows}
              rowKey={(r, i) => i}
              pagination={{ pageSize: 20 }}
              size="small"
            />
          </Card>
        </>
      )}
    </div>
  )
}

export default Sales
